import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/data/content";

const formatErr = (detail) => {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

const AdminLogin = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1428] text-white flex items-center justify-center px-5 relative overflow-hidden">
      <div className="hero-orb absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full" />
      <Link to="/" className="absolute top-6 left-6 inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors" data-testid="login-back">
        <ArrowLeft size={16} /> Back to site
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-8 md:p-10"
      >
        <div className="flex items-center gap-3 mb-8">
          <img src={BRAND.logo} alt={BRAND.name} className="h-12 w-12 rounded-lg object-contain" />
          <div>
            <div className="font-display font-bold text-lg leading-none">TITAN<span className="text-[#1E6BFF]">.</span></div>
            <div className="text-xs text-gray-500 mt-1">Admin dashboard</div>
          </div>
        </div>

        <h1 className="font-display font-bold text-3xl mb-2 flex items-center gap-2">
          <Lock size={22} className="text-[#1E6BFF]" /> Sign in
        </h1>
        <p className="text-gray-400 text-sm mb-8">Manage your booked consultation calls.</p>

        <form onSubmit={submit} className="space-y-4">
          <Input
            type="email"
            data-testid="login-email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="bg-[#050A14]/60 border-white/15 text-white placeholder:text-gray-500 focus-visible:ring-[#1E6BFF] h-12"
          />
          <Input
            type="password"
            data-testid="login-password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="bg-[#050A14]/60 border-white/15 text-white placeholder:text-gray-500 focus-visible:ring-[#1E6BFF] h-12"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1E6BFF] hover:bg-[#3B82F6] disabled:opacity-60 text-white font-semibold h-12 transition-colors duration-200 glow-blue"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : "Sign In"}
          </button>
          <div className="text-center pt-1">
            <Link to="/admin/forgot" data-testid="login-forgot-link" className="text-sm text-gray-400 hover:text-[#1E6BFF] transition-colors">
              Forgot password?
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
